<template>
  <b-modal :width="1350" :active="active" scroll="keep" :can-cancel="canCancel" @close="$emit('close')">
    <div class="p-4 bg-white rounded shadow">
      <header class="mb-4 font-thin uppercase text-blue-light">
        {{ mode === 'assignments' && activityId ? $t('add_assignments_to_group') : $t('add_activity_to_group') }}
      </header>
      <form @submit.prevent="onSave">
        <section class="mb-3">
          <label class="block mb-2">{{ mode === 'assignments' && activityId ? $t('select_assignments') : $t('select_activities') }}</label>
          <treeselect v-model="selectedTreeActivities" :multiple="true" :options="activitiesTree"
            :disable-branch-nodes="mode === 'assignments' && activityId" 
            :auto-select-children="mode !== 'assignments' || !activityId" 
            :value-consists-of="mode === 'assignments' && activityId ? 'ALL' : 'LEAF_PRIORITY'"
            append-to-body 
            :placeholder="mode === 'assignments' && activityId ? $t('select_assignments') : 'Selecciona sólo recursos'" 
            :normalizer="normalizer" 
            class="w-full"
            :maxHeight="300" 
            :limit="13" 
            :limitText="n => `+${n} ${$t('assignment')}${n > 1 ? 's' : ''}`" />
        </section>
        <footer class="pt-2 border-t border-grey-light">
          <div class="flex justify-end space-x-2">
            <v-button type="submit">{{ $t('save') }}</v-button>
            <b-button @click="close">{{ $t('close') }}</b-button>
          </div>
        </footer>
      </form>
    </div>
  </b-modal>
</template>

<script>
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'

export default {
  name: 'LinkActivitiesModal',
  components: { Treeselect },

  props: {
    active: Boolean,
    canCancel: {
      type: Array,
      default: () => ['escape', 'x', 'outside']
    },
    activities: {
      type: Array,
      default: () => []
    },
    assignments: {
      type: Array,
      default: () => []
    },
    assignedActivities: {
      type: Array,
      default: () => []
    },
    loadingActivity: Boolean,
    mode: {
      type: String,
      default: 'assignments' // 'assignments' o 'activities'
    },
    activityId: {
      type: [Number, String],
      default: null
    }
  },

  data() {
    return {
      // aquí guardamos los IDs seleccionados (p. ej. ['res-12', 'res-37', …])
      selectedTreeActivities: []
    }
  },

  computed: {
    normalizer() {
      return node => ({
        id: node.id,
        label: node.label,
        children: node.children
      })
    },

    activitiesTree() {
      // Cuando existe activityId y el modo es 'assignments', mostrar solo assignments
      // Priorizar assignments sobre activities cuando estamos en modo assignments
      if (this.activityId && this.mode === 'assignments') {
        // Si hay assignments disponibles directamente, usarlos
        if (this.assignments && this.assignments.length > 0) {
          return this.assignments.map(assignment => ({
            id: `res-${assignment.id}`,
            label: assignment.name || assignment.code || `Assignment ${assignment.id}`,
            isResource: true
          }));
        }
        
        // Si no hay assignments directos, intentar extraer de activities.resources
        const acts = Array.isArray(this.activities) ? this.activities : Object.values(this.activities);
        // Buscar la actividad que coincide con activityId y extraer sus resources
        const targetActivity = acts.find(act => act.id == this.activityId) || acts[0];
        if (targetActivity && targetActivity.resources && targetActivity.resources.length > 0) {
          return targetActivity.resources.map(resource => ({
            id: `res-${resource.id}`,
            label: resource.name || resource.code || `Assignment ${resource.id}`,
            isResource: true
          }));
        }
        return [];
      }

      // Modo normal: mostrar activities agrupadas por grupo de actividad
      const acts = Array.isArray(this.activities)
        ? this.activities
        : Object.values(this.activities);

      if (!acts || acts.length === 0) {
        return [];
      }

      // Construir mapa de nodos de actividades
      const map = {};
      acts.forEach(act => {
        map[act.id] = { id: act.id, label: act.name, children: [] };
      });

      // Deduplicar recursos globalmente y añadir como hijos de su actividad
      const globalResourceIds = new Set();
      acts.forEach(act => {
        (act.resources || []).forEach(r => {
          const rid = `res-${r.id}`;
          if (!globalResourceIds.has(rid)) {
            map[act.id].children.push({ id: rid, label: r.name, isResource: true });
            globalResourceIds.add(rid);
          }
        });
      });

      // Anidar actividades hijas bajo su actividad padre
      acts.forEach(act => {
        if (act.parent_id && map[act.parent_id]) {
          map[act.parent_id].children.push(map[act.id]);
        }
      });

      // Limpiar children vacíos en nodos de actividad
      Object.values(map).forEach(node => {
        if (!node.children.length) delete node.children;
      });

      const nestedIds = new Set(
        acts.filter(act => act.parent_id && map[act.parent_id]).map(act => act.id)
      );
      const roots = acts.filter(act => !nestedIds.has(act.id));
      const groupMap = {};
      const ungrouped = [];
      roots.forEach(act => {
        const node = map[act.id];
        if (act.group_id && act.group) {
          const rootGroup = (act.group.parent_id && act.group.parent)
            ? act.group.parent
            : act.group;
          const childGroup = (act.group.parent_id && act.group.parent)
            ? act.group
            : null;

          if (!groupMap[rootGroup.id]) {
            groupMap[rootGroup.id] = { id: `group-${rootGroup.id}`, label: rootGroup.name, children: [] };
          }

          if (childGroup) {
            // Buscar o crear el subgrupo dentro del grupo raíz
            let subGroupNode = groupMap[rootGroup.id].children.find(c => c.id === `group-${childGroup.id}`);
            if (!subGroupNode) {
              subGroupNode = { id: `group-${childGroup.id}`, label: childGroup.name, children: [] };
              groupMap[rootGroup.id].children.push(subGroupNode);
            }
            subGroupNode.children.push(node);
          } else {
            groupMap[rootGroup.id].children.push(node);
          }
        } else {
          ungrouped.push(node);
        }
      });

      return [...Object.values(groupMap), ...ungrouped].slice(0, 100);
    }

  },

  watch: {
    // Cuando cambien las assignedActivities (p. ej. al abrir),
    // copiamos directamente esos IDs al v-model del treeselect:
    assignedActivities: {
      immediate: true,
      handler(val) {
        this.selectedTreeActivities = Array.isArray(val) ? val : []
      }
    }
  },

  methods: {
    onSave() {
      if (this.mode === 'assignments' && this.activityId) {
        // Modo assignments: cuando existe activityId, solo emitir los IDs seleccionados
        // El componente padre se encargará de formatearlos correctamente
        this.$emit('save', this.selectedTreeActivities);
      } else if (this.mode === 'activities') {
        // Agrupa por actividad y emite el array [{ activity_id, assignments: [...] }]
        const activityMap = {};
        this.selectedTreeActivities.forEach(id => {
          if (typeof id === 'string' && id.startsWith('res-')) {
            const resourceId = parseInt(id.replace('res-', ''), 10);
            // Busca la actividad que contiene este recurso
            const activity = this.activities.find(act =>
              (act.resources || []).some(r => r.id === resourceId)
            );
            if (activity) {
              if (!activityMap[activity.id]) activityMap[activity.id] = [];
              activityMap[activity.id].push(resourceId);
            }
          }
        });
        const payload = Object.entries(activityMap).map(([activity_id, assignments]) => ({
          activity_id: parseInt(activity_id, 10),
          assignments
        }));
        this.$emit('save', payload);
      } else {
        // Solo IDs de mediciones
        this.$emit('save', this.selectedTreeActivities);
      }
    },
    close() {
      this.$emit('close')
    }
  }
}
</script>
